import { usePreset } from '@primeuix/themes';
import { activeBrand, customerBrand, applyBrandProfile, selectBrand } from './brandState.js';
import { resolveBrandProfile } from './brands/registry.js';
import { createShopPrimeVuePreset, resolvePrimeVuePreset } from './index.js';
import { blendColors, contrastRatio, readableBorder, readableText } from './brands/contrast.js';
import bayhive from './brands/profiles/bayhive.js';
import { brandSidebarTokens, brandSurfaceTokens, primaryButtonStates, secondaryButtonTokens } from './brands/createBrandPreset.js';

export const fonts = [
  { label: 'System font', value: 'system', family: 'system-ui, sans-serif' },
  { label: 'Inter', value: 'inter', family: '"Inter", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'Roboto', value: 'roboto', family: '"Roboto", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'Open Sans', value: 'open-sans', family: '"Open Sans", sans-serif', googleStyles: 'ital,wght@0,300..800;1,300..800' },
  { label: 'Source Sans 3', value: 'source-sans-3', family: '"Source Sans 3", sans-serif', googleStyles: 'ital,wght@0,200..900;1,200..900' },
  { label: 'Nunito Sans', value: 'nunito-sans', family: '"Nunito Sans", sans-serif', googleStyles: 'ital,wght@0,200..1000;1,200..1000' },
  { label: 'Manrope', value: 'manrope', family: '"Manrope", sans-serif', googleStyles: 'wght@200..800' },
  { label: 'Montserrat', value: 'montserrat', family: '"Montserrat", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'Raleway', value: 'raleway', family: '"Raleway", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'Work Sans', value: 'work-sans', family: '"Work Sans", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'DM Sans', value: 'dm-sans', family: '"DM Sans", sans-serif', googleStyles: 'ital,wght@0,100..1000;1,100..1000' },
  { label: 'Lora', value: 'lora', family: '"Lora", serif', googleStyles: 'ital,wght@0,400..700;1,400..700' },
  { label: 'Merriweather', value: 'merriweather', family: '"Merriweather", serif', googleStyles: 'ital,wght@0,300..900;1,300..900' },
  { label: 'Roboto Condensed', value: 'roboto-condensed', family: '"Roboto Condensed", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'Barlow Condensed', value: 'barlow-condensed', family: '"Barlow Condensed", sans-serif', googleStyles: 'ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900' },
  { label: 'IBM Plex Sans', value: 'ibm-plex-sans', family: '"IBM Plex Sans", sans-serif', googleStyles: 'ital,wght@0,100..700;1,100..700' },
  { label: 'IBM Plex Mono', value: 'ibm-plex-mono', family: '"IBM Plex Mono", monospace', googleStyles: 'ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700' },
  { label: 'Figtree', value: 'figtree', family: '"Figtree", sans-serif', googleStyles: 'ital,wght@0,300..900;1,300..900' },
  { label: 'Public Sans', value: 'public-sans', family: '"Public Sans", sans-serif', googleStyles: 'ital,wght@0,100..900;1,100..900' },
  { label: 'Source Serif 4', value: 'source-serif-4', family: '"Source Serif 4", serif', googleStyles: 'ital,wght@0,200..900;1,200..900' },
  { label: 'Roboto Mono', value: 'roboto-mono', family: '"Roboto Mono", monospace', googleStyles: 'ital,wght@0,100..700;1,100..700' },
  { label: 'Verdana', value: 'verdana', family: 'Verdana, Geneva, sans-serif' },
].sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));

export function googleFontStylesheet(value) {
  const font = fonts.find((item) => item.value === value);
  return font?.googleStyles
    ? `https://fonts.googleapis.com/css2?family=${encodeURIComponent(`${font.label}:${font.googleStyles}`)}&display=swap`
    : null;
}

export function loadShopFont(value) {
  if (typeof document === 'undefined') return;
  const href = googleFontStylesheet(value);
  if (!href) return;
  const id = `bayhive-shop-font-${value}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  // Keep fallback text readable on failure and allow a later selection to retry.
  link.addEventListener('error', () => link.remove(), { once: true });
  document.head.appendChild(link);
}

export const themeDefaults = () => ({
  primaryLight: '#4f2d7f',
  secondaryLight: '#e3a40e',
  primaryDark: '#a78bfa',
  secondaryDark: '#e3a40e',
  rounding: 20,
  font: 'system',
  useShopLogo: true,
  applyToSystem: false,
  logoLight: null,
  logoDark: null,
  logoCompactLight: null,
  logoCompactDark: null,
});
export function themeDraft(theme) {
  const draft = { ...themeDefaults(), ...theme };
  // Retired device fonts fall back without discarding the shop's colors or logos.
  if (!fonts.some((font) => font.value === draft.font)) draft.font = 'system';
  for (const mode of ['Light', 'Dark']) {
    if (theme?.[`logoCompact${mode}`] === undefined)
      draft[`logoCompact${mode}`] = theme?.logoCompact || null;
  }
  delete draft.logoCompact;
  return draft;
}
export function themeCompactLogo(theme, mode, shopLogoUrl = null) {
  const name = mode === 'dark' ? 'logoCompactDark' : 'logoCompactLight';
  const compact = theme[name] === undefined ? theme.logoCompact : theme[name];
  const main = mainLogo(theme, shopLogoUrl);
  const full = mode === 'dark' ? theme.logoDark || main : main;
  return compact || full || bayhive.logos[mode === 'dark' ? 'iconDark' : 'iconLight'];
}
export function mainLogo(theme, shopLogoUrl = null) {
  return theme?.logoLight || (theme?.useShopLogo !== false ? shopLogoUrl : null) || null;
}
export function themeLogo(theme, mode, shopLogoUrl = null) {
  const main = mainLogo(theme, shopLogoUrl);
  return (mode === 'dark' ? theme.logoDark || main : main) || bayhive.logos[mode];
}
export function themeErrors(values) {
  const errors = {};
  for (const key of ['primaryLight', 'secondaryLight', 'primaryDark', 'secondaryDark']) {
    if (!/^#[\da-f]{6}$/i.test(values[key] || ''))
      errors[key] = 'Enter a six-digit hex color, such as #51406d.';
  }
  if (!Number.isInteger(values.rounding) || values.rounding < 0 || values.rounding > 100)
    errors.rounding = 'Enter a whole percentage from 0 to 100.';
  if (!fonts.some((font) => font.value === values.font)) errors.font = 'Choose a font.';
  return errors;
}
// Percentage of half the button height; 100% is a pill without changing control size.
export const buttonRadius = (percent) =>
  `calc(${percent / 100} * (0.75em + var(--p-button-padding-y) + 1px))`;
// These persisted keys predate the distinction between brand accent and button severity.
export const brandColorField = (role, mode) =>
  `${role === 'accent' ? 'secondary' : role}${mode === 'dark' ? 'Dark' : 'Light'}`;
export const previewSurfaces = (mode, theme) =>
  brandSurfaceTokens(previewColors(theme, mode, 'primary').background, mode);
export function previewNavigation(theme, mode) {
  const primary = previewColors(theme, mode, 'primary').background;
  const accent = previewColors(theme, mode, 'accent').background;
  const sidebar = brandSidebarTokens(primary, accent, mode);
  return { background: sidebar.focusBackground, color: sidebar.activeIconColor };
}
export function previewColors(theme, mode, role) {
  const fill = theme[brandColorField(role, mode)];
  const background = /^#[\da-f]{6}$/i.test(fill || '') ? fill : '#51406d';
  return { background, color: readableText(background) };
}
export function previewButtonStates(theme, mode, role) {
  const { background } = previewColors(theme, mode, role);
  return primaryButtonStates(background, mode);
}
export function contrastReport(theme) {
  return ['light', 'dark'].flatMap((mode) =>
    ['primary', 'accent'].map((role) => {
      const { background, color } = previewColors(theme, mode, role);
      const surfaces = previewSurfaces(mode, theme);
      const surface = surfaces.content;
      const states =
        role === 'accent' ? [background] : Object.values(previewButtonStates(theme, mode, role));
      if (role === 'accent') {
        const navigation = previewNavigation(theme, mode);
        return { mode, role, icon: contrastRatio(navigation.color, navigation.background) };
      }
      const secondary = secondaryButtonTokens(background, mode, surface, surfaces.contrastSurfaces);
      const secondaryStates = [
        [secondary.background, secondary.color, secondary.borderColor],
        [secondary.hoverBackground, secondary.hoverColor, secondary.hoverBorderColor],
        [secondary.activeBackground, secondary.activeColor, secondary.activeBorderColor],
      ];
      return {
        mode,
        role,
        label: `${mode === 'light' ? 'Light' : 'Dark'} mode · Primary button`,
        text: Math.min(
          contrastRatio(background, color),
          ...secondaryStates.map(([fill, text]) => contrastRatio(fill, text)),
          ...states.map((fill) => contrastRatio(fill, readableText(fill))),
        ),
        boundary: Math.min(
          ...secondaryStates.map(([, , border]) => contrastRatio(border, surface)),
          ...states.map((fill) => contrastRatio(readableBorder(fill, surfaces.contrastSurfaces), surface)),
        ),
      };
    }),
  );
}
export function shopThemeProfile(record) {
  if (!record.theme) return null;
  const theme = themeDraft(record.theme);
  if (Object.keys(themeErrors(theme)).length) return null;
  const logo = mainLogo(theme, record.shopLogoUrl);
  const colors = Object.fromEntries(
    ['light', 'dark'].map((mode) => [
      mode,
      {
        primary: previewColors(theme, mode, 'primary').background,
        accent: previewColors(theme, mode, 'accent').background,
        contrastSurface: previewSurfaces(mode, theme).content,
      },
    ]),
  );
  return {
    ...bayhive,
    shopId: record.shopId,
    id: `shop-${record.shopId}-${record.revision}${record.inheritedRevision ? `-${record.inheritedRevision}` : ''}`,
    productName: record.shopName?.trim() || bayhive.productName,
    fontFamily: fonts.find((font) => font.value === theme.font).family,
    colors,
    logos: {
      light: logo, dark: theme.logoDark || logo,
      iconLight: themeCompactLogo(theme, 'light', record.shopLogoUrl),
      iconDark: themeCompactLogo(theme, 'dark', record.shopLogoUrl),
    },
    tokens: { components: { button: { root: { borderRadius: buttonRadius(theme.rounding) } } } },
    pdf: {
      ...bayhive.pdf,
      text: '#212529',
      muted: '#495057',
      body: '#343a40',
      border: '#ced4da',
      rowBorder: '#dee2e6',
      header: blendColors('#ffffff', theme.primaryLight, 0.08),
      accent: theme.primaryLight,
      logoUrl: logo,
      font: ['lora', 'merriweather', 'source-serif-4'].includes(theme.font)
        ? 'times'
        : ['ibm-plex-mono', 'roboto-mono'].includes(theme.font) ? 'courier' : 'helvetica',
      logo: theme.logoLight
        ? {
            data: theme.logoLight,
            format: theme.logoLight.startsWith('data:image/png') ? 'PNG' : 'JPEG',
            width: 180,
            height: 54,
          }
        : null,
    },
  };
}

let currentKey;
export function selectPageTheme(application, requested, record) {
  // Never let a BayHive shop setting alter Autoflow, including embedded legacy pages.
  const custom = application === 'bayhive' && record?.theme ? shopThemeProfile(record) : null;
  if (custom) loadShopFont(record.theme.font);
  customerBrand.value = custom || resolveBrandProfile(application, requested);
  const applyToSystem = custom && record.canApplyToSystem === true && record.theme.applyToSystem === true;
  const profile = applyToSystem ? applyBrandProfile(custom) : selectBrand(application, requested);
  return applyToSystem
    ? createShopPrimeVuePreset(profile)
    : resolvePrimeVuePreset(application, profile.id);
}
export function updatePageTheme(application, requested, record) {
  const key = JSON.stringify([application, requested, application === 'bayhive' ? record : null]);
  if (key === currentKey) return;
  usePreset(selectPageTheme(application, requested, record));
  currentKey = key;
  window.dispatchEvent(new Event('themechange'));
  // Keep the title suffix consistent after an in-place save.
  const parts = document.title.split(' - ');
  if (parts.length > 1 && application === 'bayhive') {
    parts[parts.length - 1] = activeBrand.value.productName;
    document.title = parts.join(' - ');
  }
}
